'use client';

import { useState, useMemo, useEffect } from 'react';
import { Settings } from 'lucide-react';
import ServiceFilter from '@/components/custom/ServiceFilter';
import ServiceList from '@/components/custom/ServiceList';
import { propertyServiceAPI } from '@/lib/api';

interface ServiceFilterState {
  search: string;
  status: string;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
}

interface PropertyServiceFormData {
  id?: number | string;
  name: string;
  slug: string;
  description?: string;
  image?: string;
  image_gallery?: string[];
  sort_order?: number;
  is_active?: boolean;
}

function Services() {
  const [filters, setFilters] = useState<ServiceFilterState>({
    search: '',
    status: 'all',
    sortBy: 'name',
    sortOrder: 'asc',
  });

  const [hoveredServiceId, setHoveredServiceId] = useState<string | null>(null);
  const [services, setServices] = useState<PropertyServiceFormData[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      setLoading(true);
      setError('');
      const resp = await propertyServiceAPI.getFrontendList();
      const raw = (resp as any)?.data;
      const list = (raw?.data ?? raw?.items ?? raw?.results ?? raw) as PropertyServiceFormData[];
      setServices(Array.isArray(list) ? list : []);
    } catch (error: any) {
      console.error('Error fetching services:', error);
      setError(error?.message || 'Failed to load services');
    } finally {
      setLoading(false);
    }
  };

  const filteredServices = useMemo(() => {
    let filtered = [...services];

    // Apply search filter
    if (filters.search.trim()) {
      const searchTerm = filters.search.toLowerCase().trim();
      filtered = filtered.filter(service =>
        service.name.toLowerCase().includes(searchTerm) ||
        (service.description && service.description.toLowerCase().includes(searchTerm))
      );
    }

    // Apply status filter
    if (filters.status !== 'all') {
      const isActive = filters.status === 'active';
      filtered = filtered.filter(service => service.is_active === isActive);
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aValue: any, bValue: any;

      switch (filters.sortBy) {
        case 'sort_order':
          aValue = a.sort_order ?? 0;
          bValue = b.sort_order ?? 0;
          break;
        case 'name':
        default:
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
          break;
      }

      if (aValue < bValue) return filters.sortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return filters.sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [services, filters]);

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col pt-32">
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <Settings className="w-12 h-12 text-gray-400 mx-auto mb-4 animate-spin" />
            <p className="text-gray-600">Loading services...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col pt-32">
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <Settings className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Error Loading Services</h1>
            <p className="text-gray-600">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col pt-32">
      <ServiceFilter
        filters={filters}
        onFilterChange={setFilters}
      />

      <div className="flex-1 h-[calc(100vh-200px)] container mx-auto">
        <ServiceList
          services={filteredServices}
          hoveredId={hoveredServiceId}
          onServiceHover={setHoveredServiceId}
        />
      </div>
    </div>
  );
}

export default Services;
